import { useState, useMemo, useCallback } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, isColorDark, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import { getButtonIcon, resolveIconKey } from '../buttonIcons';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function PricingCalculatorSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'simple';

    const inputs = content.inputs || [];
    const initialValues = useMemo(() => {
        const vals = {};
        inputs.forEach((inp, i) => {
            const key = `input${i + 1}`;
            vals[key] = inp.default_value != null ? parseFloat(inp.default_value) || 0 : (inp.type === 'select' && inp.options?.length ? parseFloat(inp.options[0].value) || 0 : parseFloat(inp.min) || 0);
        });
        return vals;
    }, []);

    const [values, setValues] = useState(initialValues);

    const handleChange = useCallback((key, val) => {
        setValues(prev => ({ ...prev, [key]: parseFloat(val) || 0 }));
    }, []);

    const computedResult = useMemo(() => {
        if (!content.formula) return 0;
        try {
            let formula = content.formula;
            Object.keys(values).forEach(key => {
                formula = formula.replace(new RegExp(key, 'g'), values[key]);
            });
            const result = new Function(`"use strict"; return (${formula})`)();
            return isNaN(result) ? 0 : Math.round(result * 100) / 100;
        } catch {
            return 0;
        }
    }, [content.formula, values]);

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const cardBg = s.dark ? 'rgba(255,255,255,0.06)' : '#ffffff';
    const cardBorder = s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb';
    const inputBg = s.dark ? 'rgba(255,255,255,0.08)' : '#f9fafb';
    const inputBorder = s.dark ? 'rgba(255,255,255,0.15)' : '#d1d5db';

    const sectionButtons = content.cta_text
        ? [{ text: content.cta_text, link: content.cta_link || '#contact', style: 'solid', icon: '', bg_color: '', text_color: '' }]
        : [];

    const renderInput = (inp, i) => {
        const key = `input${i + 1}`;
        const val = values[key] ?? 0;

        if (inp.type === 'range') {
            const min = parseFloat(inp.min) || 0;
            const max = parseFloat(inp.max) || 100;
            const step = parseFloat(inp.step) || 1;
            const pct = ((val - min) / (max - min)) * 100;
            return (
                <div key={i} className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                    <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium" style={{ color: s.text }}>{inp.label}</label>
                        <span className="text-sm font-bold px-3 py-1 rounded-full" style={{ backgroundColor: hexToRgba(s.accent, 0.1), color: s.accent }}>
                            {val}{inp.unit ? ` ${inp.unit}` : ''}
                        </span>
                    </div>
                    <div className="relative">
                        <input
                            type="range" min={min} max={max} step={step} value={val}
                            onChange={e => handleChange(key, e.target.value)}
                            className="w-full h-2 rounded-full appearance-none cursor-pointer"
                            style={{
                                background: `linear-gradient(to right, ${s.accent} 0%, ${s.accent} ${pct}%, ${s.dark ? 'rgba(255,255,255,0.15)' : '#e5e7eb'} ${pct}%, ${s.dark ? 'rgba(255,255,255,0.15)' : '#e5e7eb'} 100%)`,
                                accentColor: s.accent,
                            }}
                        />
                        <div className="flex justify-between mt-1">
                            <span className="text-xs" style={{ color: s.textSub }}>{min}{inp.unit ? ` ${inp.unit}` : ''}</span>
                            <span className="text-xs" style={{ color: s.textSub }}>{max}{inp.unit ? ` ${inp.unit}` : ''}</span>
                        </div>
                    </div>
                </div>
            );
        }

        if (inp.type === 'select') {
            const options = inp.options || [];
            return (
                <div key={i} className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                    <label className="text-sm font-medium mb-2 block" style={{ color: s.text }}>{inp.label}</label>
                    <select
                        value={val}
                        onChange={e => handleChange(key, e.target.value)}
                        className="w-full px-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none"
                        style={{ backgroundColor: inputBg, borderColor: inputBorder, color: s.text, outlineColor: s.accent }}
                    >
                        {options.map((opt, oi) => (
                            <option key={oi} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            );
        }

        // number input
        return (
            <div key={i} className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                <label className="text-sm font-medium mb-2 block" style={{ color: s.text }}>{inp.label}</label>
                <div className="relative">
                    <input
                        type="number"
                        min={inp.min} max={inp.max} step={inp.step || 1}
                        value={val}
                        onChange={e => handleChange(key, e.target.value)}
                        className="w-full px-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none"
                        style={{ backgroundColor: inputBg, borderColor: inputBorder, color: s.text, outlineColor: s.accent }}
                    />
                    {inp.unit && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-medium" style={{ color: s.textSub }}>{inp.unit}</span>}
                </div>
            </div>
        );
    };

    const resultDisplay = (
        <div className={`text-center p-8 rounded-2xl border transition-all duration-700 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
            style={{ backgroundColor: hexToRgba(s.accent, s.dark ? 0.12 : 0.04), borderColor: hexToRgba(s.accent, 0.2) }}>
            {content.output_label && (
                <p className="text-sm font-medium mb-2" style={{ color: s.textSub }}>{content.output_label}</p>
            )}
            <div className="flex items-baseline justify-center gap-1">
                {content.output_prefix && <span className="text-2xl font-bold" style={{ color: s.accent }}>{content.output_prefix}</span>}
                <span className="text-5xl md:text-6xl font-extrabold transition-all duration-300" style={{ color: s.accent }}>
                    {computedResult.toLocaleString()}
                </span>
                {content.output_suffix && <span className="text-lg font-medium ml-1" style={{ color: s.textSub }}>{content.output_suffix}</span>}
            </div>
        </div>
    );

    if (variant === 'detailed') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto items-start">
                        <div className="rounded-2xl border p-6 md:p-8 space-y-6" style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
                            <h3 className="text-lg font-bold" style={{ color: s.text }}>Configura tu plan</h3>
                            {inputs.map((inp, i) => renderInput(inp, i))}
                        </div>
                        <div className="lg:sticky lg:top-24 space-y-6">
                            {resultDisplay}
                            {sectionButtons.length > 0 && (
                                <div className="text-center">
                                    {sectionButtons.map((btn, i) => (
                                        <a key={i} {...resolveLinkProps(btn.link || '#')}
                                            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-semibold text-sm text-white transition-all hover:scale-105 hover:shadow-lg"
                                            style={{ backgroundColor: s.accent }}>
                                            {btn.icon && getButtonIcon(resolveIconKey(btn.icon))}
                                            {btn.text}
                                        </a>
                                    ))}
                                </div>
                            )}
                            {content.disclaimer && (
                                <p className="text-xs text-center leading-relaxed" style={{ color: s.textSub }}>{content.disclaimer}</p>
                            )}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // simple and slider-only
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className="max-w-2xl mx-auto">
                    <div className="rounded-2xl border p-6 md:p-8 space-y-6 mb-8" style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
                        {inputs.map((inp, i) => renderInput(inp, i))}
                    </div>
                    {resultDisplay}
                    {sectionButtons.length > 0 && (
                        <div className="text-center mt-6">
                            {sectionButtons.map((btn, i) => (
                                <a key={i} {...resolveLinkProps(btn.link || '#')}
                                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-semibold text-sm text-white transition-all hover:scale-105 hover:shadow-lg"
                                    style={{ backgroundColor: s.accent }}>
                                    {btn.icon && getButtonIcon(resolveIconKey(btn.icon))}
                                    {btn.text}
                                </a>
                            ))}
                        </div>
                    )}
                    {content.disclaimer && (
                        <p className="text-xs text-center mt-4 leading-relaxed" style={{ color: s.textSub }}>{content.disclaimer}</p>
                    )}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
